/**
 * Cross-Sell Engine — Recommend ancillary plans (dental, vision, life) alongside medical
 */

const CATEGORIES = {
  dental: { label: 'Dental', keywords: ['dental'] },
  vision: { label: 'Vision', keywords: ['vision'] },
  life: { label: 'Life Insurance', keywords: ['life', 'term life', 'whole life'] },
  accident: { label: 'Accident', keywords: ['accident', 'accidental'] },
  critical: { label: 'Critical Illness', keywords: ['critical illness', 'critical care'] },
};

/**
 * Detect which coverage categories are present in the selected plans
 * @param {Object[]} selectedPlans
 * @returns {Set<string>} Set of category keys that are already covered
 */
export function detectCoveredCategories(selectedPlans) {
  const covered = new Set();

  for (const plan of selectedPlans) {
    const name = (plan.planName || plan.name || '').toLowerCase();
    const category = (plan.category || '').toLowerCase();

    for (const [key, config] of Object.entries(CATEGORIES)) {
      if (config.keywords.some(kw => name.includes(kw) || category.includes(kw))) {
        covered.add(key);
      }
    }

    // Medical plans are always "covered" (they're the base)
    if (category.includes('medical') || category.includes('hmo') ||
        category.includes('ppo') || category.includes('epo')) {
      covered.add('medical');
    }
  }

  return covered;
}

/**
 * Get cross-sell recommendation categories (categories NOT covered)
 * @param {Object[]} selectedPlans
 * @param {string[]} enabledCategories - Agent's enabled cross-sell categories
 * @returns {string[]} Array of category keys to recommend
 */
export function getRecommendationCategories(selectedPlans, enabledCategories = ['dental', 'vision', 'life']) {
  const covered = detectCoveredCategories(selectedPlans);
  return enabledCategories.filter(cat => !covered.has(cat));
}

/**
 * Filter available plans to find cross-sell candidates
 * @param {Object[]} allPlans - All available plans from all sources
 * @param {string[]} categories - Categories to recommend
 * @returns {Object[]} Plans suitable for cross-sell, with crossSellCategory set
 */
export function findCrossSellPlans(allPlans, categories) {
  const results = [];

  for (const plan of allPlans) {
    const name = (plan.planName || plan.name || '').toLowerCase();
    const category = (plan.category || '').toLowerCase();

    for (const catKey of categories) {
      const config = CATEGORIES[catKey];
      if (!config) continue;

      if (config.keywords.some(kw => name.includes(kw) || category.includes(kw))) {
        results.push({
          ...plan,
          isCrossSell: true,
          crossSellCategory: catKey,
        });
        break; // Don't double-count a plan in multiple categories
      }
    }
  }

  // Sort by premium (lowest first) and limit to top 3 per category
  const byCategory = {};
  for (const plan of results) {
    const cat = plan.crossSellCategory;
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(plan);
  }

  const limited = [];
  for (const [cat, plans] of Object.entries(byCategory)) {
    plans.sort((a, b) => (a.calculatedPremium || a.premium || 0) - (b.calculatedPremium || b.premium || 0));
    limited.push(...plans.slice(0, 3));
  }

  return limited;
}

/**
 * Generate cross-sell recommendations for the current quote
 * @param {Object[]} selectedPlans - Plans the agent/client has selected
 * @param {Object[]} allAvailablePlans - All plans from all sources
 * @param {string[]} enabledCategories - Agent's cross-sell preferences
 * @returns {{ categories: string[], plans: Object[] }}
 */
export function getRecommendations(selectedPlans, allAvailablePlans, enabledCategories) {
  const categories = getRecommendationCategories(selectedPlans, enabledCategories);
  if (categories.length === 0) return { categories: [], plans: [] };

  const plans = findCrossSellPlans(allAvailablePlans, categories);
  return { categories, plans };
}

export const CrossSell = {
  detectCoveredCategories,
  getRecommendationCategories,
  findCrossSellPlans,
  getRecommendations,
  CATEGORIES,
};
